/**
 * paid-2 (Symptom Tracking) view-model + builder.
 *
 * Source: `userData` current-scan `symptoms` object (per-symptom 0–10 ratings).
 * Many scans never completed the symptom module → builder returns
 * `available:false` and the panel shows an empty/baseline state.
 *
 * Shown ratings are real scan data. ⚠️ Burden composite formula + cohort
 * averages are unvalidated — OPEN_ISSUES.md OI-11/OI-12.
 */
import { Band } from '../../results-vm';
import { CohortAverages, symptomBand } from '../../results-placeholders';
import { SYMPTOM_CLUSTERS, SYMPTOM_META_IDS, HORMONE_META_IDS } from './symptom-clusters.data';
import { TOOLS_BY_ID } from '../../tools/tools.data';

export interface SymptomBarVM {
  labelKey: string; // symptomsForm.symptoms.<id>.name
  value: number; // 0–10
  color: string;
}

/** One symptom inside a cluster chip/detail (real rating + co-occurrence). */
export interface ClusterSymptomVM {
  id: string;
  labelKey: string; // symptomsForm.symptoms.<id>.name
  rating: number; // 0–10 (real, from the scan)
  color: string;
  /** other reported symptoms in the same cluster */
  alongside: string[]; // labelKeys
  /** hormone-education keys, present only for symptoms in SYMPTOM_META_IDS */
  hormoneKey?: string;
  stageKey?: string;
  bodyKey?: string;
  /**
   * catalog-description key, present for every symptom
   * (symptomsForm.symptoms.<id>.description). Used as the popup body fallback
   * so a tap never opens an empty card, even for symptoms with no meta text.
   */
  descKey: string;
}

/** A symptom cluster that actually emerged from the user's report. */
export interface ClusterVM {
  id: string;
  labelKey: string;
  color: string;
  /** "stood out" for the heaviest cluster, "also reported" for the rest */
  badgeKey: string;
  insightTitleKey: string;
  insightBodyKey: string;
  /** tool name keys surfaced as "tools that can help" */
  toolNames: string[];
  symptoms: ClusterSymptomVM[];
}
export interface DomainScoreVM {
  labelKey: string;
  value: number; // 0–10
  avg: number | null;
  color: string;
}
export interface SymptomsPanelVM {
  available: boolean;
  score: number | null; // burden 0–100
  band: Band;
  /** band-specific hero description key (lower burden = lighter load) */
  copy1Key: string;
  reportedCount: number;
  buckets: { pronounced: number; moderate: number; mild: number };
  domains: DomainScoreVM[];
  topSymptoms: SymptomBarVM[];
  clusters: ClusterVM[];
  /** tool-matrix category keys the user actually has symptoms in */
  toolCategoryKeys: string[];
}

/** cluster id → tools-matrix category key (sleep / mood / body) */
const CLUSTER_TO_CATEGORY: Record<string, string> = {
  sleep_energy: 'sleep',
  mood_mind: 'mood',
  body: 'body',
};

/** symptom-id → domain (to finalise with product — OPEN_ISSUES.md OI-11). */
const DOMAINS: { key: string; labelKey: string; ids: string[] }[] = [
  { key: 'sleep', labelKey: 'results.sym.domains.sleep', ids: ['insomnia_falling_asleep', 'insomnia_staying_asleep', 'early_awakening', 'night_sweats', 'unrefreshing_sleep', 'daytime_sleepiness'] },
  { key: 'energy', labelKey: 'results.sym.domains.energy', ids: ['energy_dips', 'motivation_drop', 'weight_gain'] },
  { key: 'cognition', labelKey: 'results.sym.domains.cognition', ids: ['brain_fog', 'forgetfulness', 'difficulty_concentrating', 'slow_recall'] },
  { key: 'mood', labelKey: 'results.sym.domains.mood', ids: ['mood_swings', 'anxiety', 'low_mood', 'irritability', 'stress_sensitivity'] },
];

const barColor = (v10: number): string => (v10 >= 5.5 ? '#e05a6e' : v10 >= 3.5 ? '#f59d00' : '#1f8079');

/** hero copy variant, keyed to the symptom band (thresholds MIRROR BAND_CONFIG.symptoms:
 *  wordfirst sec 3 opening values 34 / 67, so word + hero copy + 3-level bar agree). */
function symptomCopyKey(burden: number): string {
  if (burden < 34) return 'results.sym.copy1.light';
  if (burden < 67) return 'results.sym.copy1.medium';
  return 'results.sym.copy1.heavy';
}

function avg(vals: number[]): number {
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
}

const symLabel = (id: string) => `symptomsForm.symptoms.${id}.name`;

/**
 * Group the user's *reported* symptoms into clusters. Each symptom lands in the
 * first cluster (priority order) that lists it; clusters with no reported
 * symptom are dropped; the heaviest cluster (by total severity) gets the
 * "stood out" badge. All ratings + co-occurrence are real scan data.
 */
function buildClusters(symptoms: Record<string, number> | null): ClusterVM[] {
  const map = symptoms || {};
  const assigned = new Set<string>();

  const grouped = SYMPTOM_CLUSTERS.map((def) => {
    const syms = def.symptomIds
      .filter((id) => !assigned.has(id))
      .map((id) => ({ id, v: Number(map[id]) || 0 }))
      .filter((x) => x.v > 0);
    syms.forEach((s) => assigned.add(s.id));
    return { def, syms };
  }).filter((c) => c.syms.length > 0);

  const total = (syms: { v: number }[]) => syms.reduce((a, s) => a + s.v, 0);
  grouped.sort((a, b) => total(b.syms) - total(a.syms));

  return grouped.map((c, idx) => ({
    id: c.def.id,
    labelKey: c.def.labelKey,
    color: c.def.color,
    badgeKey: idx === 0 ? 'results.sym.clusters.badgePrimary' : 'results.sym.clusters.badgeSecondary',
    insightTitleKey: c.def.titleKey,
    insightBodyKey: c.def.bodyKey,
    toolNames: c.def.toolIds.map((id) => TOOLS_BY_ID[id]?.labelKey).filter(Boolean) as string[],
    symptoms: c.syms
      .slice()
      .sort((a, b) => b.v - a.v)
      .map((s) => ({
        id: s.id,
        labelKey: symLabel(s.id),
        rating: s.v,
        color: barColor(s.v),
        alongside: c.syms.filter((o) => o.id !== s.id).map((o) => symLabel(o.id)),
        hormoneKey: HORMONE_META_IDS.has(s.id) ? `results.sym.meta.${s.id}.hormone` : undefined,
        stageKey: HORMONE_META_IDS.has(s.id) ? `results.sym.meta.${s.id}.stage` : undefined,
        bodyKey: SYMPTOM_META_IDS.has(s.id) ? `results.sym.meta.${s.id}.body` : undefined,
        descKey: `symptomsForm.symptoms.${s.id}.description`,
      })),
  }));
}

export function buildSymptomsVM(
  symptoms: Record<string, number> | null,
  cohort: CohortAverages | null,
  showComparisons = true,
  nActive = 60
): SymptomsPanelVM {
  const entries = symptoms
    ? Object.entries(symptoms).filter(([, v]) => typeof v === 'number')
    : [];
  const available = entries.length > 0;

  const nonZero = entries.filter(([, v]) => v > 0);
  const reportedCount = nonZero.length;
  const values = nonZero.map(([, v]) => v);

  const buckets = {
    pronounced: values.filter((v) => v >= 7).length,
    moderate: values.filter((v) => v > 4 && v < 7).length,
    mild: values.filter((v) => v > 0 && v <= 4).length,
  };

  // Symptom load = severity-tiered burden built from the pronounced/moderate/
  // mild counts shown right below the ring, so the headline number tracks the
  // breakdown. Weight by tier, scale to 0–100 against a saturation cap.
  //
  // Picked over a plain mean because both means failed at the extremes:
  //   • avg of *reported only* (÷ reported) → one strong symptom read as Heavy.
  //   • avg of *all surveyed* (÷ ~60, zeros included) → real pronounced
  //     symptoms diluted down to "Light" (e.g. 3 pronounced → 7/100).
  // The tier weights + cap are unvalidated — needs product/clinical sign-off
  // (OPEN_ISSUES.md OI-11).
  const LOAD_WEIGHTS = { pronounced: 3, moderate: 2, mild: 1 };
  // Saturation curve (handoff TASK-SYMPTOM-SCORE-1): the load approaches a soft
  // ceiling and never reaches 100, so a heavy load no longer reads as a flat
  // "100". Replaces the old min(100, points/30*100) hard cap that clustered
  // every heavy load at the top. MUST mirror the server cohort average
  // (server/.../scan-cohort.controller.ts symptomBurden) so the comparison bar
  // stays on the same scale. Anchor: S=16 -> ~53 (Medium).
  const LOAD_CEILING = 97; // soft max — a literal 100 is never shown
  // K scales with the ACTIVE catalog size (05_SCORING_100_items.md, section 4b):
  // S grows with the number of items she can mark, so K must grow in the same
  // ratio or the same woman scores higher on a bigger catalog. nActive = catalog
  // size at scan time (defaults to 60 until it is stored per-scan). N=60 -> K=20
  // (identical to today); N=100 -> K=33.33. LOAD_K_BASE is the config knob to
  // recalibrate on real data. MUST stay mirrored with report-view + scan-cohort.
  const LOAD_K_BASE = 20; // calibration constant, tuned to a 60-item catalog
  const CATALOG_BASELINE = 60;
  const LOAD_K = LOAD_K_BASE * (nActive / CATALOG_BASELINE); // ramp
  const weightedPoints =
    buckets.pronounced * LOAD_WEIGHTS.pronounced +
    buckets.moderate * LOAD_WEIGHTS.moderate +
    buckets.mild * LOAD_WEIGHTS.mild;
  const burden = available
    ? weightedPoints <= 0
      ? 0
      : Math.max(
          1,
          Math.round(LOAD_CEILING * (1 - Math.exp(-weightedPoints / LOAD_K))),
        )
    : null;

  // Cohort comparison rail removed from symptoms (design.md within-person;
  // decided 08-26, shipped wordfirst 9ג). Kept only on the hormonal imprint.
  const clusters = buildClusters(symptoms);
  const toolCategoryKeys = clusters
    .map((c) => CLUSTER_TO_CATEGORY[c.id])
    .filter((k): k is string => !!k);

  const map = symptoms || {};
  const domains: DomainScoreVM[] = DOMAINS.map((d) => {
    const vals = d.ids.map((id) => Number(map[id]) || 0).filter((v) => v > 0);
    const v = +avg(vals).toFixed(1);
    return { labelKey: d.labelKey, value: v, avg: null, color: barColor(v) };
  }).filter((d) => d.value > 0);

  const topSymptoms: SymptomBarVM[] = nonZero
    .sort((a, b) => b[1] - a[1])
    .slice(0, 7)
    .map(([id, v]) => ({ labelKey: `symptomsForm.symptoms.${id}.name`, value: v, color: barColor(v) }));

  return {
    available,
    score: burden,
    band: burden != null ? symptomBand(burden) : symptomBand(0),
    copy1Key: burden != null ? symptomCopyKey(burden) : 'results.sym.copy1.light',
    reportedCount,
    buckets,
    domains,
    topSymptoms,
    clusters,
    toolCategoryKeys,
  };
}
